// packages/web/src/components/HeroBanner.tsx
import React from 'react'
import { Sparkles, ArrowRight, Award } from 'lucide-react'
import { useSiteConfig } from '../context/SiteConfigContext'
import { useCategoriesWithProducts } from '../lib/storeCategories'

import { Link } from '@tanstack/react-router'
import { assetSrc } from '../lib/assets'

const FALLBACK_SHOWCASE =
  'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=1200&q=80'

interface HeroBannerProps {
  onSelectCategory: (category: string | undefined) => void
  selectedCategory?: string | undefined
  showcaseImage?: string | null
}

export const HeroBanner: React.FC<HeroBannerProps> = ({
  onSelectCategory,
  showcaseImage,
}) => {
  const { config } = useSiteConfig()
  const { categories } = useCategoriesWithProducts()
  const defaultCategory = categories[0]?.slug

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-[var(--bg-main)] via-[var(--bg-surface)] to-[var(--bg-main)] border-b border-[var(--gold-main)]/20 py-8 sm:py-16">
      {/* Background Decorative Gold Ambient Glows */}
      <div className="absolute top-10 left-1/4 w-[300px] sm:w-[500px] h-[200px] sm:h-[300px] bg-[var(--gold-main)]/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute bottom-5 right-1/4 w-[250px] sm:w-[400px] h-[150px] sm:h-[250px] bg-[var(--gold-dark)]/10 blur-[90px] rounded-full pointer-events-none" />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-8 lg:px-12 relative z-10">
        {/* Responsive Ticker Strip */}
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 bg-[var(--bg-surface-2)]/80 border border-[var(--gold-main)]/25 p-3 rounded-2xl backdrop-blur-md shadow-lg text-[10px] sm:text-xs">
          <div className="flex flex-wrap items-center gap-2 font-mono">
            <span className="flex h-2 w-2 relative shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[var(--gold-main)] font-bold uppercase tracking-wider">STORE GUARANTEE:</span>
            <span className="bg-[var(--bg-main)] px-2.5 py-0.5 rounded-full border border-[var(--border)] text-[var(--text-primary)] font-sans">
              {config.guarantee_badge_text}
            </span>
          </div>

          <Link
            to="/quality"
            className="text-[10px] sm:text-[11px] text-[var(--gold-main)] hover:underline font-semibold flex items-center gap-1 shrink-0"
          >
            <Award className="w-3.5 h-3.5" />
            Quality &amp; Guarantee Terms &rarr;
          </Link>
        </div>

        {/* Hero Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 items-center">
          {/* Left Column Text */}
          <div className="lg:col-span-7 space-y-4 sm:space-y-6 text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--gold-main)]/15 border border-[var(--gold-main)]/35 text-[10px] sm:text-xs font-bold text-[var(--gold-light)]">
              <Sparkles className="w-3 h-3 text-[var(--gold-main)]" />
              <span className="uppercase">{config.brand_name} • {config.brand_tagline}</span>
            </div>

            <h1 className="text-2xl sm:text-5xl lg:text-6xl font-title font-bold text-[var(--text-primary)] tracking-tight leading-tight">
              {config.hero_title_line1} <br />
              <span className="gold-shimmer-text">{config.hero_title_line2}</span>
            </h1>

            <p className="text-xs sm:text-base text-[var(--text-muted)] font-sans font-light leading-relaxed max-w-2xl">
              {config.hero_subtext}
            </p>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-4 pt-1">
              <a href="#catalog" className="btn-gold text-xs sm:text-sm py-2.5 sm:py-3.5 px-4 sm:px-7 shadow-xl flex items-center justify-center gap-1 group">
                <span>{config.hero_cta_primary || 'Explore Collection'}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition" />
              </a>

              <button
                onClick={() => defaultCategory && onSelectCategory(defaultCategory)}
                disabled={!defaultCategory}
                className="btn-gold-outline text-xs sm:text-sm py-2.5 sm:py-3.5 px-4 sm:px-6 flex items-center justify-center disabled:opacity-50"
              >
                <span>{config.hero_cta_secondary || 'Browse Categories'}</span>
              </button>
            </div>

            {/* Dynamic Trust Stats Bar — only show configured values from SiteConfig */}
            <div className="pt-4 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-3 gap-2 text-left">
              {config.hero_stat1_value && config.hero_stat1_label && (
                <div>
                  <div className="text-base sm:text-2xl font-title font-bold gold-shimmer-text">
                    {config.hero_stat1_value}
                  </div>
                  <div className="text-[9px] sm:text-[11px] text-[var(--text-faint)] uppercase tracking-wider mt-0.5">
                    {config.hero_stat1_label}
                  </div>
                </div>
              )}

              {config.hero_stat2_value && config.hero_stat2_label && (
                <div>
                  <div className="text-base sm:text-2xl font-title font-bold text-[var(--text-primary)]">
                    {config.hero_stat2_value}
                  </div>
                  <div className="text-[9px] sm:text-[11px] text-[var(--text-faint)] uppercase tracking-wider mt-0.5">
                    {config.hero_stat2_label}
                  </div>
                </div>
              )}

              {config.hero_stat3_value && config.hero_stat3_label && (
                <div>
                  <div className="text-base sm:text-2xl font-title font-bold text-[var(--text-primary)]">
                    {config.hero_stat3_value}
                  </div>
                  <div className="text-[9px] sm:text-[11px] text-[var(--text-faint)] uppercase tracking-wider mt-0.5">
                    {config.hero_stat3_label}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 mt-4 lg:mt-0 min-w-0">
            <img
              src={assetSrc(
                config.hero_showcase_image.trim() || showcaseImage || FALLBACK_SHOWCASE,
              )}
              alt={config.hero_showcase_title || config.brand_name}
              className="w-full aspect-[4/3] object-cover rounded-2xl"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
